import { Box, ThemeProvider, Tooltip, Typography } from "@mui/material";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { Card } from "react-bootstrap";
import { addButtonTextStyle, fontSize } from "./tableStyles";
import { materialReactTableThemeDark } from "../../utility/Utility";
import Button from "../UI/Button";
import { useRef } from "react";
import { generateCsv, download, mkConfig } from 'export-to-csv';

const DisplayTable = ({
  tableData,
  columns,
  keyName,
  onAddTop = () => {},
  onAddBottom = () => {},
  showAddButtonTop = false,
  showAddButtonBottom = false,
  showExportButton = false,
  showFullScreenOption = true,
  initialState = {},
  newButtonNameTop = "New",
  newButtonNameBottom = "Save",
}) => {
  const rowVirtualizerInstanceRef = useRef(null);

  const csvConfig = mkConfig({
    fieldSeparator: ',',
    decimalSeparator: '.',
    useKeysAsHeaders: true,
    filename: "results_import_errors",
    columnHeaders: ['row_number', 'ps_code', 'collection_date', 'visit_ids', 'error']
  });

  const handleExportData = () => {
    const csv = generateCsv(csvConfig)(tableData);
    download(csvConfig)(csv);
  };

  const table = useMaterialReactTable({
    columns: columns,
    data: tableData,

    getRowId: (row) => row[keyName],

    initialState: {
      density: "compact",
      ...initialState,
    },

    enableColumnActions: true,
    enableDensityToggle: false,
    enableFilters: true,
    enableHiding: false,

    //sticky header and footer feature allows you to keep the header and footer of the table visible while scrolling through the table
    enableStickyHeader: true,
    enableStickyFooter: false,

    enablePagination: false,
    enableFullScreenToggle: showFullScreenOption,
    enableTopToolbar: true,
    enableBottomToolbar: true,

    //enabling the virtualization on row to render a large number of rows without major performance issues
    enableRowVirtualization: true,

    rowVirtualizerInstanceRef,
    rowVirtualizerOptions: { overscan: 5 },

    //css for header
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: "#035157",
        color: "#ffffff",
        fontWeight: "normal",
        fontSize: fontSize,
      },
    },

    //css for the table container
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        padding: "5px",
      },
    },

    //css for toolbar
    muiTopToolbarProps: {
      sx: {
        backgroundColor: "#035157",
        ...(!showAddButtonTop && { justifyContent: "end" }), //when no add button available, align toolbar options on the right
      },
    },

    //css for the table data container
    muiTableContainerProps: {
      sx: { height: "80%" },
    },

    //css for the table data
    muiTableBodyProps: {
      sx: () => ({
        '& tr:nth-of-type(odd):not([data-selected="true"]):not([data-pinned="true"]) > td':
          {
            backgroundColor: "rgb(220,241,242,0.7)",
            fontSize: fontSize,
          },
        '& tr:nth-of-type(odd):not([data-selected="true"]):not([data-pinned="true"]):hover > td':
          {
            backgroundColor: "rgb(0,177,176,0.4)",
            fontSize: fontSize,
          },
        '& tr:nth-of-type(even):not([data-selected="true"]):not([data-pinned="true"]) > td':
          {
            backgroundColor: "#FFFFFF",
            fontSize: fontSize,
          },
        '& tr:nth-of-type(even):not([data-selected="true"]):not([data-pinned="true"]):hover > td':
          {
            backgroundColor: "rgb(0,177,176,0.4)",
            fontSize: fontSize,
          },
      }),
    },

    //css for table bottom bar

    muiBottomToolbarProps: {
      sx: {
        ...(!showAddButtonBottom && { justifyContent: "end" }), //when no add button available, align toolbar options on the right
      },
    },

    //rendering toptoolbar action button
    renderTopToolbarCustomActions: () => {
      return (
        showAddButtonTop && (
          <Tooltip title="New Record">
            <div onClick={onAddTop}>
              <Card.Img
                variant="top"
                className="wh28px cPointer"
                src={
                  process.env.REACT_UI_URL +
                  "/images/ctswwt-action-icon-add.svg"
                }
              />
              <span style={addButtonTextStyle}>{newButtonNameTop}</span>
            </div>
          </Tooltip>
        )
      );
    },

    //rendering bottomtoolbar action button
    renderTopToolbarCustomActions: () => {
      return (
        showExportButton && (
          <Button
            isPrimaryButton={true}
            onClick={handleExportData}
          >
            Export
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-66 ml-2"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
            </svg>
          </Button>
        )
      );
    },


    renderBottomToolbarCustomActions: () => {
      return (
        showAddButtonBottom && (
          <Button isPrimaryButton={true} onClick={onAddBottom}>
            {newButtonNameBottom}
          </Button>
        )
      );
    },
  });

  return (
    // <div> //Add this div to match the height of the rows
    <ThemeProvider theme={materialReactTableThemeDark}>
      <MaterialReactTable table={table} />
    </ThemeProvider>
    // </div>
  );
};

export default DisplayTable;
